import React, {useEffect, useState, useCallback, useRef} from "react";
//local
import cn from "classnames";
import {notNull} from "helpers/helper";

// every mounted closable LocalModal. A nested modal is rendered inside its parent`s
// node, so containment is what tells which modal a click or Esc belongs to
const openModals = new Set();

const closeModalsOutside = (target) =>
  openModals.forEach(
    (modal) => modal.isChildrenLoaded && !modal.node.contains(target) && modal.close()
  );

export const LocalModal = ({
  title,
  titleIcon,
  children,
  onClose,
  className,
  style,
  isChildrenLoaded = true,
}) => {
  const [node, setNode] = useState(null);
  const ref = useCallback((element) => notNull(element) && setNode(element), []);

  // the listeners are attached once, so they read the fresh props through this entry
  const self = useRef({});
  self.current.node = node;
  self.current.close = onClose;
  self.current.isChildrenLoaded = isChildrenLoaded;

  useEffect(() => {
    if (!onClose || !node) return;
    const modal = self.current;

    // Esc belongs to the innermost open modal: the one holding no other modal
    const isInnermost = () =>
      ![...openModals].some((other) => other !== modal && node.contains(other.node));

    const closeOnClickOutside = (event) =>
      modal.isChildrenLoaded && !node.contains(event.target) && modal.close();
    const closeOnEsc = (event) =>
      event.keyCode === 27 && isInnermost() && modal.close();
    // a click inside this modal never reaches the document listener - the wrapper
    // stops it - so the modals it falls outside of, a nested one above all, are
    // closed from here. Capture phase, to outrun any stopPropagation on the way up
    const closeNestedOnClick = (event) => closeModalsOutside(event.target);

    // deferred, so that the click which opened the modal does not close it at once
    const timer = setTimeout(() => {
      openModals.add(modal);
      node.addEventListener("click", closeNestedOnClick, true);
      document.addEventListener("click", closeOnClickOutside);
      document.addEventListener("keydown", closeOnEsc);
    }, 0);

    return () => {
      clearTimeout(timer);
      openModals.delete(modal);
      node.removeEventListener("click", closeNestedOnClick, true);
      document.removeEventListener("click", closeOnClickOutside);
      document.removeEventListener("keydown", closeOnEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node]);

  return (
    <div
      ref={ref}
      className={cn("modal-ent__wrapper __local", {[className]: !!className})}
      onClick={(e) => e.stopPropagation()}
      style={style}
    >
      {(title || titleIcon) && (
        <div className="modal-ent__title __local">
          {titleIcon}
          {title}
        </div>
      )}
      {children}
    </div>
  );
};
